import { useEffect } from 'react';
import { useSEO } from '../../../utils/seo';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import WhatsAppButton from '../../home/components/WhatsAppButton';

export default function SanzimanTamirPage() {
  useSEO({
    title: 'Şanzıman Tamir ve Bakım | Denizli Araç Servisi',
    description: 'Denizli\'de otomatik ve manuel şanzıman tamir ve bakım hizmetleri. DSG, CVT şanzıman uzmanlığı. Debriyaj değişimi, şanzıman revizyonu ve yağ bakımı. Profesyonel hizmet garantisi.',
    keywords: 'denizli şanzıman tamiri, denizli otomatik vites tamiri, denizli debriyaj değişimi, denizli DSG tamiri, denizli şanzıman bakımı, şanzıman revizyonu denizli',
    canonical: '/blog/sanziman-tamir-bakim',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Şanzıman Tamir & Bakım',
      description: 'Otomatik ve manuel şanzıman konusunda uzman hizmet. DSG ve CVT şanzıman uzmanlığı.',
      author: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
      },
      publisher: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/5946f8dc6d1689a139aced3671fb829b/5627d08f05dce4eda6468c71266b611d.png',
        },
      },
      datePublished: '2024-01-15',
      dateModified: new Date().toISOString().split('T')[0],
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/sanziman-tamir-bakim`,
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/3fb1130cbef1b52d3200ece8c0bb85da.jpg"
          alt="Şanzıman Tamiri"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="font-serif text-5xl lg:text-6xl font-bold mb-6">Şanzıman Tamir &amp; Bakım</h1>
          <p className="font-sans text-xl text-gray-200">Otomatik ve manuel şanzıman konusunda uzman hizmet</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          {/* Introduction */}
          <div className="mb-16">
            <p className="font-sans text-lg text-gray-700 leading-relaxed mb-6">
              Şanzıman, aracınızın motor gücünü tekerleklere ileten ve sürüş konforunu belirleyen kritik bir sistemdir. Hem otomatik hem de manuel şanzımanlar, düzenli bakım ve doğru onarım gerektirir. Servisimiz, 15 yıllık deneyimi ve özellikle üst segment araçlardaki uzmanlığıyla, tüm şanzıman sorunlarınıza profesyonel çözümler sunar.
            </p>
          </div>

          {/* Şanzıman Sorunları */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Şanzıman Sorunlarının Belirtileri</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-4">
              Şanzıman sorunları erken tespit edildiğinde, büyük hasarlar ve maliyetli onarımlar önlenebilir. Dikkat edilmesi gereken belirtiler:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-alert-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Vites değiştirirken sarsıntı, gecikme veya anormal sesler</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-alert-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Otomatik viteste vites atlamaları veya kayma hissi</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-alert-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Şanzıman yağı altından sızıntı veya yanık kokusu</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-alert-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Debriyaj pedalında sertlik veya kayma</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-alert-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Gösterge panelinde şanzıman arıza lambası yanması</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-alert-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Vites değiştirirken gürültü veya titreşim</span>
              </li>
            </ul>
          </div>

          {/* Image */}
          <div className="mb-16 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=detailed%20automotive%20transmission%20components%20and%20gears%20laid%20out%20on%20clean%20workbench%2C%20precision%20measurement%20tools%2C%20automatic%20gearbox%20parts%2C%20professional%20transmission%20rebuild%20components%2C%20technical%20workshop%20setting&width=1200&height=600&seq=blog-sanziman-002&orientation=landscape"
              alt="Şanzıman Parçaları"
              className="w-full h-96 object-cover"
            />
          </div>

          {/* Otomatik Şanzıman */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Otomatik Şanzıman Bakım ve Onarımı</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              Otomatik şanzımanlar, karmaşık hidrolik ve elektronik sistemler içerir. Düzenli bakım ve profesyonel onarım, şanzımanın ömrünü önemli ölçüde uzatır.
            </p>
            
            <div className="space-y-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-drop-line text-accent-gold text-2xl mr-3"></i>
                  Şanzıman Yağı Değişimi
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Otomatik şanzıman yağı, sistemin düzgün çalışması için kritiktir. Üretici önerilerine göre (genellikle 60.000-80.000 km'de) yağ ve filtre değişimi yapılmalıdır. Servisimizde, orijinal spesifikasyonlara uygun yağlar kullanılır ve sistem tamamen temizlenir.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-computer-line text-accent-gold text-2xl mr-3"></i>
                  Elektronik Kontrol Ünitesi (TCU) Teşhisi
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Modern otomatik şanzımanlar, elektronik kontrol üniteleri tarafından yönetilir. Marka özel teşhis cihazlarımızla, TCU hata kodları okunur, sensör değerleri kontrol edilir ve gerekirse yazılım güncellemeleri yapılır.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-tools-line text-accent-gold text-2xl mr-3"></i>
                  Şanzıman Revizyonu
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Ciddi sorunlarda, şanzıman tamamen sökülür ve revize edilir. Aşınmış diskler, bantlar, contalar ve yatak keçeleri yenilenir. Valf gövdesi temizlenir veya değiştirilir. Tork konvertörü kontrol edilir. Montaj sonrası kapsamlı testler yapılır.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-settings-3-line text-accent-gold text-2xl mr-3"></i>
                  Adaptasyon ve Kalibrasyon
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Onarım veya bakım sonrası, şanzıman adaptasyon işlemine tabi tutulur. Bu işlem, şanzımanın yeni parçalara alışmasını ve optimal performans göstermesini sağlar. Vites geçiş noktaları ve basınç değerleri kalibre edilir.
                </p>
              </div>
            </div>
          </div>

          {/* Manuel Şanzıman */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Manuel Şanzıman Bakım ve Onarımı</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              Manuel şanzımanlar daha basit yapıda olsa da, düzenli bakım ve doğru kullanım gerektirir. Sunduğumuz hizmetler:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-disc-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Debriyaj Değişimi</h4>
                <p className="font-sans text-sm text-gray-600">Debriyaj balata, baskı ve rulman değişimi, volant taşlama</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-oil-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Şanzıman Yağı Bakımı</h4>
                <p className="font-sans text-sm text-gray-600">Periyodik yağ değişimi ve seviye kontrolü</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-settings-4-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Senkromeç Tamiri</h4>
                <p className="font-sans text-sm text-gray-600">Vites geçiş sorunlarının giderilmesi</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-refresh-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Şanzıman Revizyonu</h4>
                <p className="font-sans text-sm text-gray-600">Komple sökme, parça değişimi ve montaj</p>
              </div>
            </div>
          </div>

          {/* DSG ve CVT */}
          <div className="mb-16 bg-primary-dark rounded-2xl p-10">
            <h2 className="font-serif text-3xl font-bold text-white mb-6">DSG ve CVT Şanzıman Uzmanlığı</h2>
            <p className="font-sans text-base text-gray-300 leading-relaxed mb-4">
              Çift kavramalı (DSG) ve sürekli değişken (CVT) şanzımanlar, özel bilgi ve ekipman gerektirir. Servisimizde:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span><strong>DSG Şanzımanlar:</strong> Mekatronik ünite tamiri, çift kavrama değişimi, yağ ve filtre bakımı</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span><strong>CVT Şanzımanlar:</strong> Kayış veya zincir değişimi, hidrolik sistem bakımı, elektronik kontrol</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span><strong>Özel Teşhis:</strong> Marka özel yazılımlar ve cihazlarla detaylı analiz</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span><strong>Yazılım Güncellemeleri:</strong> Üretici tarafından yayınlanan güncellemelerin uygulanması</span>
              </li>
            </ul>
          </div>

          {/* Garanti ve Güvenilirlik */}
          <div className="mb-16 bg-primary-dark rounded-2xl p-10">
            <h2 className="font-serif text-3xl font-bold text-white mb-6">Güvenilirlik ve Kalite Garantisi</h2>
            <p className="font-sans text-base text-gray-300 leading-relaxed mb-4">
              Şanzıman tamir ve bakım hizmetlerimizde kalite ve güvenilirlik önceliğimizdir:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Orijinal veya OEM parça kullanımı garantisi</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Şanzıman uzmanı teknisyen kadrosu</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Son teknoloji teşhis ve tamir ekipmanları</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Detaylı test sürüşü ve kalite kontrol</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Tüm işlemler e-karneye kaydedilir</span>
              </li>
            </ul>
          </div>

          {/* Bakım Önerileri */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Şanzıman Ömrünü Uzatma İpuçları</h2>
            <div className="bg-gray-50 p-8 rounded-xl">
              <ul className="space-y-4">
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Üretici önerilerine göre düzenli yağ değişimi yaptırın</span>
                </li>
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Aracınızı ısınmadan zorlamayın, özellikle soğuk havalarda</span>
                </li>
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Ani gaz ve fren hareketlerinden kaçının</span>
                </li>
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Otomatik viteste, durma anında N konumuna geçmeyin</span>
                </li>
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Anormal ses veya davranışlarda hemen kontrol ettirin</span>
                </li>
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Ağır yük çekerken veya römork kullanırken ekstra dikkatli olun</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gray-50 rounded-2xl p-10 text-center">
            <h3 className="font-serif text-3xl font-bold text-gray-900 mb-4">Şanzımanınızda Sorun mu Var?</h3>
            <p className="font-sans text-base text-gray-700 mb-8 max-w-2xl mx-auto">
              Ücretsiz şanzıman kontrolü için hemen randevu alın. Erken teşhis, büyük masrafları önler. Uzman kadromuz size en doğru çözümü sunacaktır.
            </p>
            <a
              href="/#appointment"
              className="inline-block bg-accent-gold text-primary-dark px-8 py-4 rounded-lg font-sans font-semibold hover:bg-primary-dark hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              Ücretsiz Şanzıman Kontrolü
            </a>
          </div>

        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}